import { Avatar, Button, Flex, Text, type TextProps } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import classes from './chat-bubble.module.css';

/** `self` is the side of the current user, `other` the side of the recipient. */
export type ChatSide = 'self' | 'other';

type SideProps = { side: ChatSide; children: ReactNode };

const directionOf = (side: ChatSide) =>
  side === 'self' ? 'row' : 'row-reverse';

export const ChatBubble = ({ side, children }: SideProps) => (
  <Flex
    px={16}
    py={12}
    gap={16}
    w='90%'
    direction='column'
    data-side={side}
    className={classes.bubble}
  >
    {children}
  </Flex>
);

/** Top row of a bubble; the recipient's row is mirrored. */
export const ChatBubbleHeader = ({
  side,
  wrap,
  children,
}: SideProps & { wrap?: 'wrap' | 'nowrap' }) => (
  <Flex
    align='center'
    justify='space-between'
    direction={directionOf(side)}
    wrap={wrap}
  >
    {children}
  </Flex>
);

/** Sender and time, mirrored for the recipient. */
export const ChatMessageMeta = ({ side, children }: SideProps) => (
  <Flex gap={16} align='center' direction={directionOf(side)}>
    {children}
  </Flex>
);

export const ChatMessageTime = ({ time }: { time: string }) => (
  <Text size='sm' className={classes.time} c='#5A5967'>
    {dayjs(time).format('DD.MM.YYYY HH:mm')}
  </Text>
);

export type ChatSenderProps = {
  name: ReactNode;
  /** Shown next to the name, for example the assistant's avatar. */
  avatarSrc?: string;
  avatarPosition?: 'start' | 'end';
};

export const ChatSender = ({
  name,
  avatarSrc,
  avatarPosition = 'start',
}: ChatSenderProps) => {
  const avatar = avatarSrc ? <Avatar size={24} src={avatarSrc} alt='' /> : null;

  return (
    <Flex gap={8} align='center'>
      {avatarPosition === 'start' && avatar}
      <Text size='sm'>{name}</Text>
      {avatarPosition === 'end' && avatar}
    </Flex>
  );
};

export type ChatListenButtonProps = {
  label: string;
  /** Without audio the button keeps its place but is invisible and inactive. */
  audioUrl: string | undefined;
  onListen: (audioUrl: string) => void;
  labelWeight?: TextProps['fw'];
};

export const ChatListenButton = ({
  label,
  audioUrl,
  onListen,
  labelWeight,
}: ChatListenButtonProps) => (
  <Button
    onClick={() => audioUrl && onListen(audioUrl)}
    c='#1F86FF'
    className={classes.listen}
    data-has-audio={!!audioUrl}
    p={0}
    size='sm'
    variant='unstyled'
    disabled={!audioUrl}
  >
    <IconPlayerPlay size={16} />
    <Text ml={8} size='sm' fw={labelWeight}>
      {label}
    </Text>
  </Button>
);
